<template>
  <div class="c-risk-range-selector">
    <div class="c-risk-range-selector-top">
      <div class="c-risk-range-selector-top-heading">
        <div class="c-risk-range-selector-top-heading-title">
          {{ title }}
        </div>
        <QIcon name="info">
          <BaseStreemTooltip
            anchor="top middle"
            self="bottom middle"
          >
            {{ tooltipText }}
          </BaseStreemTooltip>
        </QIcon>
      </div>
      <div class="c-risk-range-selector-top-sub-heading">
        {{ subTitle }}
      </div>
    </div>
    <div class="c-risk-range-selector-body">
      <span class="c-risk-range-selector-body-range-label">
        {{ $t("risk_range_selector.low") }}
      </span>
      <QRange
        :key="rangeKey"
        v-model="handleRiskRange"
        :disable="disabled"
        marker-labels
        :max="100"
        :min="0"
        :step="25"
      >
        <template #marker-label-group="scope">
          <div
            v-for="marker in scope.markerList"
            :key="marker.index"
            :class="[
              marker.classes,
              'c-risk-range-selector-body-range-marker-label',
            ]"
            :style="marker.style as StyleValue"
          >
            {{ marker.value }}
          </div>
        </template>
      </QRange>
      <span class="c-risk-range-selector-body-range-label">{{
        getLocaleText("risk_range_selector.high")
      }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type StyleValue } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseStreemTooltip } from "shared/components/base";
import { type StreamFiltersMinMax } from "shared/helpers/StreamFilters";

export interface RiskRangeSelectorProps {
  disabled?: boolean;
  modelValue: StreamFiltersMinMax;
  subTitlePrefix: string;
  title: string;
  tooltipText: string;
}

interface RiskRangeSelectorEmits {
  (e: "update:modelValue", value: StreamFiltersMinMax): void;
}

const props = defineProps<RiskRangeSelectorProps>();

const emit = defineEmits<RiskRangeSelectorEmits>();

const rangeKey = ref(1);

const handleRiskRange = computed<StreamFiltersMinMax>({
  get() {
    return props.modelValue;
  },
  set(val: StreamFiltersMinMax) {
    let range = val;

    if (val.min === val.max) {
      range = props.modelValue;
      rangeKey.value += 1;
    }

    emit("update:modelValue", range);
  },
});

defineExpose({ rangeKey });

const subTitle = computed(() =>
  getLocaleText("risk_range_selector.sub_title", {
    subTitlePrefix: props.subTitlePrefix,
    min: props.modelValue.min,
    max: props.modelValue.max,
  })
);
</script>

<style scoped lang="scss">
.c-risk-range-selector {
  display: flex;
  flex-direction: column;
  row-gap: 18px;
  padding-bottom: 25px;

  &-top {
    display: flex;
    flex-direction: column;
    row-gap: 8px;

    &-heading {
      display: flex;
      align-items: center;
      column-gap: 8px;

      &-title {
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
      }
    }

    &-sub-heading {
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      color: var(--s-color-denim-8);
    }
  }

  &-body {
    display: flex;
    column-gap: 15px;

    &-range-label {
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      color: var(--s-color-denim-8);
      text-transform: uppercase;
      padding-top: 7px;
    }

    &-range-marker-label {
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      color: var(--s-color-denim-7);
    }
  }
}
</style>
